import styles from './TableRow.module.scss';

import clsx from 'clsx';
import produce from 'immer';
import React, { forwardRef, ReactNode, useEffect, useMemo } from 'react';
import { TableCommonProps } from 'react-table';

import { remToPx } from '@work4all/data/lib/hooks/useRemToPx';

import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';

import { reactRefSetter } from '@work4all/utils';

import { useInifiteList } from '../../InfiniteList';
import {
  DropZoneType,
  EditTableEntry,
  EditTablePositionKind,
} from '../../types';
import { useEditTable } from '../edit-table-provider/EditTableProvider';
import { TableCell } from '../TableCell/TableCell';

import { DRAG_HANDLER_COLUMN_WIDTH } from './constants';
import { DragHandlerIconButton } from './DragHandlerIconButton';
import { useEditTableDrag } from './use-edit-table-drag';
import { ResizableContext, useResizable } from './use-resizable';

export type ITableRowProps = TableCommonProps & {
  disabled?: boolean;
  localId: string;
  index: number;
  onMoveRow?: (
    localId: string,
    index: number,
    dropZoneType: DropZoneType
  ) => void;
  children?: ReactNode;
  kind?: EditTablePositionKind;
  onClickOutside?: (localId: string) => void;
  isNormal?: boolean;
  canDrop: (input: { id: string; index: number }) => number;
  getHovered?: () => EditTableEntry;
  getCurrent?: (id: string) => EditTableEntry;
  isMoveAllowed?: (from: EditTableEntry, to: EditTableEntry) => boolean;
  hasBorder?: {
    vertical: boolean;
    horizontal: boolean;
  };
  isSimpleMode?: boolean;
  isMenuVisible?: boolean;
  isDragHandlerVisible?: boolean;
  isDragHandlerClickable?: boolean;
  onClick?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >['onClick'];
};

const paddingsOfParents = remToPx(0.5);

const TableRow = (
  props: ITableRowProps,
  forwardedRef: React.ForwardedRef<HTMLDivElement>
) => {
  const {
    disabled = false,
    localId,
    index,
    onMoveRow: moveRowDone,
    className,
    style,
    children,
    kind,
    canDrop,
    getHovered,
    getCurrent,
    isMoveAllowed,
    onClickOutside,
    isNormal = true,
    hasBorder = { vertical: false, horizontal: false },
    isSimpleMode,
    isMenuVisible,
    isDragHandlerVisible,
    isDragHandlerClickable,
    ...rest
  } = props;

  const { onDropItem } = useEditTable();

  const { direction, isDragging, isOver, dropRef, dragRef, dropZone } =
    useEditTableDrag({
      canDrop,
      index,
      localId,
      moveRowDone,
      getHovered,
      getCurrent,
      isMoveAllowed,
      onBeforeDrag: () => {
        onClickOutside?.(localId);
      },
      onDropItem,
    });

  const modifiedStyle = produce(style, (draft) => {
    draft.width =
      (typeof draft.width === 'number' ? draft.width : parseInt(draft.width)) +
      DRAG_HANDLER_COLUMN_WIDTH;
    draft.opacity = isDragging ? 0 : 1;
    draft.height =
      kind === ErpPositionsKind.SEITENUMBRUCH &&
      typeof draft.height === 'number'
        ? draft.height + 1
        : draft.height;
  });

  const orientation = direction === 'up' ? '0 4px 0px 0px' : '0 -4px 0px 0px';
  const overStyle: React.CSSProperties =
    isOver && dropZone !== 'inside'
      ? {
          boxShadow: orientation + ' inset var(--brand01)',
        }
      : {};

  const horizontalWidth =
    !isSimpleMode && kind === ErpPositionsKind.SEITENUMBRUCH
      ? 2
      : hasBorder?.horizontal
      ? 1
      : 0;
  const verticalWidth =
    !isSimpleMode && kind === ErpPositionsKind.SEITENUMBRUCH
      ? 2
      : hasBorder?.vertical
      ? 1
      : 0;

  const border: React.CSSProperties = {
    borderBottomStyle: 'solid',
    borderColor: 'var(--ui04)',
    borderTopWidth: horizontalWidth,
    borderBottomWidth: horizontalWidth,
    borderLeftWidth: verticalWidth,
    borderRightWidth: verticalWidth,
    marginTop: kind === ErpPositionsKind.SEITENUMBRUCH ? -1 : 0,
    width: '100%', // isSimpleMode ? 'fit-content' : '100%',
    ...overStyle,
  };

  const elId = `position_row_${localId}`;

  useEffect(() => {
    const onClick = (evt) => {
      const flyoutEl = document.getElementById(`${elId}`);
      let targetEl = evt.target as HTMLElement; // clicked element

      do {
        if (targetEl === flyoutEl || targetEl.id === elId) {
          return;
        }
        targetEl = targetEl.parentElement;
      } while (targetEl && targetEl?.nodeName !== 'BODY');
      if (targetEl?.nodeName === 'BODY') onClickOutside?.(localId);
    };

    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('click', onClick);
    };
  }, [localId, elId, onClickOutside]);

  const { onAddPosition } = useEditTable();
  const handleAdd = (offset = 0) => {
    onAddPosition({
      positionType: ErpPositionsKind.TEXTZEILE,
      article: { id: 0 },
      index: index + offset,
      cacheOnly: true,
    });
  };

  const resizable = useResizable();
  const {
    handleElement,
    resizableRef,
    style: resizableStyle,
    height,
  } = resizable;
  const { measureMax } = useInifiteList();

  useEffect(() => {
    // remove static offset of paddings
    if (height) measureMax(index, height - paddingsOfParents);
  }, [index, measureMax, height]);

  const resizableValue = useMemo(() => {
    const setHeight = (e: number | undefined) => {
      if (e === undefined) {
        measureMax(index, null);
        resizable.setHeight(null);
      } else {
        resizable.setHeight(e);
      }
    };

    // Handle auto height rows directly
    const effectiveHeight =
      modifiedStyle?.height === 'auto' ? null : resizable.height;

    return {
      ...resizable,
      height: effectiveHeight,
      setHeight,
    };
  }, [resizable, measureMax, index, modifiedStyle?.height]);

  return (
    <ResizableContext.Provider value={resizableValue}>
      <div
        id={elId}
        key={elId}
        ref={reactRefSetter(forwardedRef, dropRef, resizableRef)}
        className={clsx(styles['table-row'], className, {
          [styles.borderHorizontal]: hasBorder?.vertical,
        })}
        style={{
          ...modifiedStyle,
          ...border,
          ...resizableStyle,
          minHeight: 'calc(2.25rem + 2px)',
        }}
        {...rest}
        onClick={(e) => {
          if (disabled) {
            return;
          }

          rest.onClick?.(e);
        }}
      >
        {isOver && dropZone === 'inside' && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'var(--brand01)',
            }}
          />
        )}
        {!isMenuVisible || disabled ? null : !moveRowDone ? (
          <TableCell className={styles['drag-empty']} disabled={disabled} />
        ) : (
          <TableCell
            ref={dragRef}
            role="cell"
            className={clsx(styles['drag-handler'], {
              [styles['drag-handler-simple']]: isSimpleMode,
            })}
            kind={kind}
            disabled={disabled}
          >
            {isDragHandlerVisible && (
              <DragHandlerIconButton
                onAddAfter={
                  isNormal && isDragHandlerClickable
                    ? () => {
                        handleAdd(1);
                      }
                    : undefined
                }
                onAddBefore={
                  isNormal && isDragHandlerClickable
                    ? () => {
                        handleAdd();
                      }
                    : undefined
                }
              />
            )}
          </TableCell>
        )}

        {children}
        {handleElement}
      </div>
    </ResizableContext.Provider>
  );
};

const ForwardRefTableRow = forwardRef(TableRow);

export { ForwardRefTableRow as TableRow };
